import React from 'react';
import s from './s.module.less';
import dayjs, { Dayjs } from 'dayjs';
import DatePicker from 'components/DatePicker';
import { classifyTimeSlots, getDayKey, isMobile } from 'utils/common';
import { Spin } from 'antd';

type Prop = {
    onChange?: (value: number) => void;
    timeSlotList?: number[];
    defaultValue?: number;
    loading?: boolean;
    fullScreen?: boolean,
    currentMobileHeight?: number,
    smallHeight?: boolean,
    idSub?: string,
    hideNav?: boolean,
    extraTop?: number
    isPatientPortalLayout?: boolean;
    timezone?: string,
};

type TTimeInfo = {
    weekDay: string,
    dayKey: string,
    hasSlot?: boolean,
    firstTimeSlot: number,
};

const timeGroupMap = {
    earlyMorning: 'Early Morning',
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',
};

type TTimeSlots = { dayKey: string, timeSlots: number[] };

type TSlotsGroup = {
    earlyMorning: number[],
    morning: number[],
    afternoon: number[],
    evening: number[],
};

type TTimeSlotsGroup = {
    dayKey: string,
    hasSlot: boolean,
    slotsGroup: TSlotsGroup,
};
const randomId = () => {
    return Math.random().toString(36).substring(2);
};

export default function TimePicker(props: Prop) {
    const { isPatientPortalLayout, onChange, timeSlotList, defaultValue, loading, fullScreen, extraTop = 0, currentMobileHeight, smallHeight, idSub, timezone } = props;
    const isMobileUsing = isMobile();
    const [defaultDate, setDefaultDate] = React.useState<Dayjs>(dayjs());
    const [currentDate, setCurrentDate] = React.useState<Dayjs>(defaultDate);
    const [pickedTime, setPickedTime] = React.useState<number>(0);
    const [dateTimeslotMap, setDateTimeslotMap] = React.useState<{ [key: string]: number[] }>({});

    const timeLabelWrapId = React.useRef(idSub ? `${idSub}_time-label-wrap` : 'time-label-wrap');
    const wholeTimeLabelId = React.useRef(idSub ? `${idSub}_whole-time-label` : 'whole-time-label');
    const slotItemWrapId = React.useRef(idSub ? `${idSub}_slot-item-wrap` : 'slot-item-wrap');

    const [firstTimeSlot, setFirstTimeSlot] = React.useState<number>(0);
    const [allTimeList, setAllTimeList] = React.useState<TTimeInfo[]>([]);

    const [timeLabelWrapWidth, setTimeLabelWrapWidth] = React.useState(0);
    const [wholeTimeLabelWidth, setWholeTimeLabelWidth] = React.useState(0);
    const pendingEmptyDayKeys = React.useRef<string[]>([]);

    const [offsetX, setOffsetX] = React.useState(0);
    const changeFromDatePickFlag = React.useRef(false);
    const [mobileHeight, setMobileHeight] = React.useState(0);

    const [mobileOffsetX, setMobileOffsetX] = React.useState(0);
    const [mobileOffsetY, setMobileOffsetY] = React.useState(0);

    const [hasDoInit, setHasDoInit] = React.useState(false);

    const latestScrollXTime = React.useRef<number>(0);
    const latestScrollYTime = React.useRef<number>(0);
    const scrollXTimer = React.useRef<number>(0);
    const scrollYTimer = React.useRef<number>(0);

    const timeXInterval = 200;
    const timeYInterval = 100;

    const touchType = React.useRef<'label' | 'slot' | undefined>();

    const timeLabeItemWidth = timeLabelWrapWidth / 7;
    const offsetXInx = Math.floor(offsetX / timeLabeItemWidth);
    const canLeft = offsetX > 0;
    const canRight = offsetX + timeLabelWrapWidth < wholeTimeLabelWidth - 20;

    const mobileXInx = timeLabeItemWidth ? Math.floor(mobileOffsetX / timeLabeItemWidth) : 0;

    React.useEffect(() => {
        if (isMobileUsing && mobileXInx < allTimeList.length && touchType.current === 'label') {
            const target = allTimeList[mobileXInx];
            const dayKey = target?.dayKey;
            const find = document.getElementsByClassName(dayKey);
            if (dayKey) {
                setDefaultDate(dayjs(dayKey));
            }
            const timeSlotWrap = document.getElementById(slotItemWrapId.current);
            if (find && find.length > 0 && timeSlotWrap) {
                const offsetY = (find[0] as HTMLElement).offsetTop;
                if (scrollYTimer.current) {
                    window.clearTimeout(scrollYTimer.current);
                }
                const leftTime = Date.now() - latestScrollYTime.current;
                if (leftTime > timeYInterval) {
                    timeSlotWrap.scrollTo({ top: offsetY, behavior: 'smooth' });
                    latestScrollYTime.current = Date.now();
                } else {
                    scrollYTimer.current = window.setTimeout(() => {
                        timeSlotWrap.scrollTo({ top: offsetY, behavior: 'smooth' });
                    }, timeYInterval - leftTime);
                }
            }
        }
    }, [mobileXInx, isMobileUsing]);

    React.useEffect(() => {
        if (isMobileUsing && touchType.current === 'slot') {
            const allSlotItem = document.getElementsByClassName(s.slotDay);
            if (allSlotItem) {
                let targetDayKey = '';
                for (let i = 0; i < allSlotItem.length; i++) {
                    const item = allSlotItem[i] as HTMLElement;
                    if (item.offsetTop > mobileOffsetY) {
                        const targetItem = i > 0 ? allSlotItem[i - 1] : item;
                        const dayKeys = targetItem.classList;
                        if (dayKeys && dayKeys.length > 1) {
                            targetDayKey = dayKeys[1];
                            break;
                        }
                    }
                }

                if (targetDayKey) {
                    setDefaultDate(dayjs(targetDayKey));
                }

                const xInx = allTimeList.findIndex((timeInfo) => timeInfo.dayKey === targetDayKey);
                const timeLabelWrap = document.getElementById(timeLabelWrapId.current);

                if (xInx >= 0 && timeLabelWrap) {
                    const offsetX = xInx * timeLabeItemWidth;
                    if (scrollYTimer.current) {
                        window.clearTimeout(scrollXTimer.current);
                    }
                    const leftTime = Date.now() - latestScrollXTime.current;
                    if (leftTime > timeXInterval) {
                        timeLabelWrap.scrollTo({ left: offsetX, behavior: 'smooth' });
                        latestScrollXTime.current = Date.now();
                    } else {
                        scrollXTimer.current = window.setTimeout(() => {
                            timeLabelWrap.scrollTo({ left: offsetX, behavior: 'smooth' });
                        }, timeXInterval - leftTime);
                    }
                }
            }
        }
    }, [mobileOffsetY, isMobileUsing, timeLabeItemWidth]);

    React.useEffect(() => {
        const timeLabelWrap = document.getElementById(timeLabelWrapId.current);
        const listenX = (e: Event) => {
            const x = (e.target as HTMLElement)?.scrollLeft || 0;
            // console.log({
            //     x
            // })
            setMobileOffsetX(x);
        };
        if (timeLabelWrap) {
            timeLabelWrap.addEventListener('scroll', listenX);
        }
        const listenY = (e: Event) => {
            const y = (e.target as HTMLElement)?.scrollTop || 0;
            // console.log({
            //     y
            // })
            setMobileOffsetY(y);
        };
        const timeSlotWrap = document.getElementById(slotItemWrapId.current);
        if (timeSlotWrap) {
            timeSlotWrap.addEventListener('scroll', listenY);
        }
        // console.log({
        //     timeLabelWrap,
        //     timeSlotWrap
        // })
        return () => {
            const timeLabelWrap = document.getElementById(timeLabelWrapId.current);
            const timeSlotWrap = document.getElementById(slotItemWrapId.current);
            if (timeLabelWrap) {
                timeLabelWrap.removeEventListener('scroll', listenX);
            }
            if (timeSlotWrap) {
                timeSlotWrap.removeEventListener('scroll', listenY);
            }
        };
    }, []);

    React.useEffect(() => {
        const setFullscreenHeight = () => {
            setMobileHeight(window.innerHeight);
        };
        window.addEventListener('resize', setFullscreenHeight);
        window.addEventListener('orientationchange', setFullscreenHeight);
        setFullscreenHeight();
        return () => {
            window.removeEventListener('resize', setFullscreenHeight);
            window.removeEventListener('orientationchange', setFullscreenHeight);
        };
    }, []);

    React.useEffect(() => {
        if (offsetXInx < allTimeList.length && !isMobileUsing) {
            const newFirstTimeInfo = allTimeList[offsetXInx];
            setCurrentDate(dayjs(newFirstTimeInfo.firstTimeSlot));
            if (changeFromDatePickFlag.current !== true) {
                setDefaultDate(dayjs(newFirstTimeInfo.firstTimeSlot));
            }
            changeFromDatePickFlag.current = false;
            pendingEmptyDayKeys.current = [];
            const target = document.getElementById(slotItemWrapId.current);
            if (target) {
                target.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            }
        }
    }, [offsetXInx, isMobileUsing]);

    React.useEffect(() => {
        const timeLabelWrap = document.getElementById(timeLabelWrapId.current);
        const wholeTimeLabel = document.getElementById(wholeTimeLabelId.current);

        setTimeLabelWrapWidth(timeLabelWrap?.clientWidth || 0);
        setWholeTimeLabelWidth(wholeTimeLabel?.scrollWidth || 0);
    }, [allTimeList]);

    React.useEffect(() => {
        if (!timeSlotList || timeSlotList.length === 0) return;
        if (Object.keys(dateTimeslotMap).length === 0) return;

        const firstAllowTime = dayjs(timeSlotList[0]);
        const lastAllowTime = dayjs(timeSlotList[timeSlotList.length - 1]);
        const firstAllowTimeOfWeek = firstAllowTime.day();
        const startDay = firstAllowTime.subtract(firstAllowTimeOfWeek, 'day');
        const _allTimeList: TTimeInfo[] = [];
        let isEnd = false;
        let currentDay = startDay;
        const find = timeSlotList.find((time) => getDayKey(dayjs(time)) === getDayKey(currentDay));
        _allTimeList.push({
            weekDay: currentDay.format('ddd'),
            dayKey: getDayKey(currentDay),
            hasSlot: !!find,
            firstTimeSlot: currentDay.valueOf(),
        });
        for (let i = 0; i < 70; i++) {
            currentDay = currentDay.add(1, 'day');
            if (currentDay.isAfter(lastAllowTime)) {
                isEnd = true;
            }
            const _find = timeSlotList.find((time) => getDayKey(dayjs(time)) === getDayKey(currentDay));
            _allTimeList.push({
                weekDay: currentDay.format('ddd'),
                dayKey: getDayKey(currentDay),
                hasSlot: !!_find,
                firstTimeSlot: currentDay.valueOf(),
            });
            if (isEnd && currentDay.day() === 6) break;
        }
        setAllTimeList(_allTimeList);
    }, [timeSlotList, dateTimeslotMap]);

    React.useEffect(() => {
        if (hasDoInit || allTimeList.length === 0) {
            return;
        }
        if (defaultValue) {
            setDefaultDate(dayjs(defaultValue));
            setCurrentDate(dayjs(defaultValue));
            setPickedTime(defaultValue);
            scrollToTargetDay(dayjs(defaultValue), 'auto');
        }
        setHasDoInit(true);
    }, [defaultValue, hasDoInit, allTimeList]);

    React.useEffect(() => {
        if (timeSlotList && timeSlotList.length > 0) {
            setFirstTimeSlot(timeSlotList[0]);
            const map: { [key: string]: number[] } = {};
            timeSlotList.forEach((time) => {
                const key = getDayKey(dayjs(time));
                if (!map[key]) {
                    map[key] = [];
                }
                map[key].push(time);
            });
            setDateTimeslotMap(map);
        }
        const defalutTime = timeSlotList?.find((time) => dayjs(time).isAfter(dayjs()));
        if (defalutTime && !defaultValue) {
            setDefaultDate(dayjs(defalutTime));
            setCurrentDate(dayjs(defalutTime));
        }
    }, [timeSlotList, defaultValue]);

    const onLeft = () => {
        let target = offsetX - timeLabelWrapWidth;
        if (target < 0) {
            target = 0;
        }
        setOffsetX(target);
        const timeLabelWrap = document.getElementById(timeLabelWrapId.current);
        if (timeLabelWrap) {
            timeLabelWrap.scrollTo({
                left: target,
                behavior: 'smooth',
            });
        }
    };

    const onRight = () => {
        let target = offsetX + timeLabelWrapWidth;
        if (target + timeLabeItemWidth > wholeTimeLabelWidth) {
            target = wholeTimeLabelWidth - timeLabelWrapWidth;
        }

        setOffsetX(target);
        const timeLabelWrap = document.getElementById(timeLabelWrapId.current);
        if (timeLabelWrap) {
            timeLabelWrap.scrollTo({
                left: target,
                behavior: 'smooth',
            });
        }
    };

    const handleDayClickPc = (dayKey: string) => {
        // 之前只是禁掉了isMobile，但是在dashboard里，会出现很多timeslot的情况，如果不能定位，用户会滚得很辛苦。
        if (isMobileUsing && !isPatientPortalLayout) {
            return;
        }
        try {
            const find = document.getElementsByClassName(dayKey);
            if (find && find.length > 0) {
                const offsetY = (find[0] as HTMLElement).offsetTop;
                const timeSlotWrap = document.getElementById(slotItemWrapId.current);
                if (timeSlotWrap) {
                    timeSlotWrap.scrollTo({ top: offsetY, behavior: 'smooth' });
                }
            }
        } catch (e) {
            console.error(e);
        }
    };

    const scrollToTargetDay = (dayVal: dayjs.Dayjs, behavior?: ScrollBehavior) => {
        const targetIndex = allTimeList.findIndex((timeInfo) => timeInfo.dayKey === getDayKey(dayVal));
        if (targetIndex === -1) return;
        const targetTimeInfo = allTimeList[targetIndex];
        const weekDayIndex = dayjs(targetTimeInfo.dayKey).day();
        let targetIndexInWrap = isMobileUsing ? targetIndex : targetIndex;
        if (targetIndexInWrap < 0) {
            targetIndexInWrap = 0;
        }
        const target = targetIndexInWrap * timeLabeItemWidth;
        setOffsetX(target);
        touchType.current = undefined;
        const timeLabelWrap = document.getElementById(timeLabelWrapId.current);
        if (timeLabelWrap) {
            timeLabelWrap.scrollTo({
                left: target,
                behavior: behavior || 'smooth',
            });
        }
        if (isMobileUsing && targetIndex < allTimeList.length) {
            const targetItem = allTimeList[targetIndex];
            if (targetItem) {
                const { dayKey } = targetItem;
                const find = document.getElementsByClassName(dayKey);
                if (find && find.length > 0) {
                    const offsetY = (find[0] as HTMLElement).offsetTop;
                    const timeSlotWrap = document.getElementById(slotItemWrapId.current);
                    if (timeSlotWrap) {
                        timeSlotWrap.scrollTo({ top: offsetY, behavior: behavior || 'smooth' });
                    }
                }
            }
        }
    };

    const getDayKeysUnitDisplay = (dayKeys: string[]) => {
        if (!dayKeys || dayKeys.length === 0) {
            return;
        }
        const isToday = dayjs(dayKeys[0]).isSame(dayjs(), 'day');
        const firstLay = isToday ? 'Today' : `${dayjs(dayKeys[0]).format('ddd')}`;
        const lastDay = dayjs(dayKeys[dayKeys.length - 1]);
        if (dayKeys.length === 1) {
            return `${firstLay}, ${dayjs(dayKeys[0]).format('MMMM DD, YYYY')}`;
        }
        const isSameMonth = dayjs(dayKeys[0]).isSame(dayjs(dayKeys[dayKeys.length - 1]), 'month');
        return `${firstLay}, ${dayjs(dayKeys[0]).format('MMMM DD')} - ${lastDay.format(`${isSameMonth ? '' : 'MMMM '}DD, YYYY`)}`;
    };

    const options = { timeZoneName: 'long' };
    let timeZone = '';
    try {
        timeZone = `Timezone : ${Intl.DateTimeFormat(undefined, options as any).resolvedOptions().timeZone}`;
        const offsetMinutes = new Date().getTimezoneOffset();
        const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
        const offsetPrefix = offsetMinutes < 0 ? '+' : '-';
        const offsetString = `${offsetPrefix}${offsetHours.toString().padStart(2, '0')}:00`;
        timeZone = `${timeZone} ${offsetString}`;
    } catch (e) {
        console.error(e);
    }
    const currentDateTimeSlots: TTimeSlots[] = []; // dateTimeslotMap[getDayKey(currentDate)] || [];
    const targetDays = isMobileUsing ? allTimeList.slice() : allTimeList.slice(offsetXInx, offsetXInx + 7);
    targetDays.forEach((timeInfo) => {
        currentDateTimeSlots.push({
            dayKey: timeInfo.dayKey,
            timeSlots: dateTimeslotMap[timeInfo.dayKey] || [],
        });
    });

    // if (hideNav) {
    //     allHeightExpectScrollSlot = allHeightExpectScrollSlot - 72;
    // }

    //const maxHeight = mobileHeight ? `${(mobileHeight - allHeightExpectScrollSlot)}px` : '40vh';

    // console.log({
    //     mobileHeight,
    //     allHeightExpectScrollSlot,
    //     smallHeight,
    //     hideNav,
    //     maxHeight
    // })

    let maxHeight = window.document.body.clientHeight * 0.82 - 40 - 50 - 56 - 64;
    if (extraTop) {
        maxHeight -= extraTop;
    }

    const renderTimeSlotsGroup = (group: TSlotsGroup) => {
        return Object.entries(group).map(([key, timeSlots]: [string, number[]]) => {
            return timeSlots && timeSlots.length > 0 && (
                <>
                    <div className={s.slotGroupTitle}>{timeGroupMap[key as keyof typeof timeGroupMap]}</div>
                    <div key={key} className={s.slotItemsWrap}>
                        {
                            timeSlots?.map((time) => {
                                const isSelect = time === pickedTime;
                                const dayTime = timezone ? dayjs(time).tz(timezone) : dayjs(time);
                                return (
                                    <div
                                        key={time}
                                        className={`${s.slotTime} ${isSelect ? s.slotTimeSelect : ''}`}
                                        onClick={() => {
                                            setPickedTime(time);
                                            onChange && onChange(time);
                                        }}
                                    >
                                        {dayTime.format('h:mm A')}
                                    </div>
                                );
                            })
                        }
                    </div>
                </>
            );
        });
    };

    return (
        <div className={`${s.wrap} ${fullScreen ? s.wrapFull : ''} ${isPatientPortalLayout ? s.wrapInPortal : ''}`}>
            {/* <div style={{ position: 'absolute' }}>
                <p>{mobileOffsetY}</p>
                <p>{mobileOffsetX}</p>
                <p>{touchType.current || 'null'}</p>
                <p>{mobileXInx}</p>
            </div> */}
            <div className={s.dateWrap}>
                <DatePicker
                    defaultValue={defaultDate}
                    onChange={(val) => {
                        setCurrentDate(val);
                        changeFromDatePickFlag.current = true;
                        scrollToTargetDay(val);
                    }}
                    startDate={dayjs()}
                    endDate={dayjs().add(2, 'month')}
                    availableDates={Object.keys(dateTimeslotMap)}
                />
            </div>
            <div className={s.weekDayList}>
                <div
                    onClick={() => {
                        if (canLeft) {
                            onLeft();
                        }
                    }}
                    className={`${s.left} ${canLeft ? s.canLeft : ''}`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div
                    className={s.middle}
                    id={timeLabelWrapId.current}
                >
                    <div
                        onTouchStart={() => {
                            touchType.current = 'label';
                        }}
                        className={s.middleInner}
                        id={wholeTimeLabelId.current}
                    >
                        {
                            allTimeList.map((timeInfo) => {
                                return (
                                    <div
                                        key={timeInfo.dayKey}
                                        className={`${s.weekDay} ${timeInfo.hasSlot ? s.weekDayValued : ''}`}
                                        // 之前只是禁掉了isMobile，但是在dashboard里，会出现很多timeslot的情况，如果不能定位，用户会滚得很辛苦。
                                        onClick={(!isMobileUsing || isPatientPortalLayout) ? () => {
                                            handleDayClickPc(timeInfo.dayKey);
                                        } : undefined}
                                    >
                                        <div className={s.weekDayName}>
                                            {timeInfo.weekDay}
                                        </div>
                                        <div className={s.weekDayDate}>
                                            {dayjs(timeInfo.dayKey).format('DD')}
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
                <div
                    onClick={() => {
                        if (canRight) {
                            onRight();
                        }
                    }}
                    className={`${s.right} ${canRight ? s.canRight : ''}`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </div>
            <div
                className={s.slotWrap}
            >
                <div
                    className={s.slotWrapIn}
                    id={slotItemWrapId.current}
                    style={{ maxHeight: `${maxHeight}px` }}
                >
                    <Spin spinning={loading === true}>
                        <div
                            className={s.slotList}
                            style={{ minHeight: '120px' }}
                            onTouchStart={() => {
                                touchType.current = 'slot';
                            }}
                        >
                            {
                                classifyTimeSlots(currentDateTimeSlots).map((group: TTimeSlotsGroup, inx) => {
                                    const { dayKey, slotsGroup, hasSlot } = group;
                                    const isLast = inx === currentDateTimeSlots.length - 1;
                                    if (!hasSlot) {
                                        pendingEmptyDayKeys.current.push(dayKey);
                                        if (!isLast) {
                                            return null;
                                        } else {
                                            const pendingDisplayDayKeys: string[] = [...pendingEmptyDayKeys.current];
                                            pendingEmptyDayKeys.current = [];
                                            return (
                                                <div className={`${s.slotDay} ${pendingDisplayDayKeys?.join(' ')}`} key={dayKey}>
                                                    <div className={s.slotDayName}>{getDayKeysUnitDisplay(pendingDisplayDayKeys)}</div>
                                                    <div className={s.slotEmpty} key={dayKey}>No availability</div>
                                                </div>
                                            );
                                        }
                                    }
                                    const pendingDisplayDayKeys: string[] = [...pendingEmptyDayKeys.current];
                                    pendingEmptyDayKeys.current = [];
                                    return (
                                        <>
                                            {
                                                pendingDisplayDayKeys && pendingDisplayDayKeys.length > 0 &&
                                                <div className={`${s.slotDay} ${pendingDisplayDayKeys?.join(' ')}`} key={getDayKeysUnitDisplay(pendingDisplayDayKeys)}>
                                                    <div className={s.slotDayName}>{getDayKeysUnitDisplay(pendingDisplayDayKeys)}</div>
                                                    <div className={s.slotEmpty} key={dayKey}>No availability</div>
                                                </div>
                                            }
                                            <div className={`${s.slotDay} ${dayKey}`} key={dayKey}>
                                                <div className={s.slotDayName}>{getDayKeysUnitDisplay([dayKey])}</div>
                                                {
                                                    !hasSlot ? (
                                                        <div className={s.slotEmpty} key={dayKey}>No availability</div>
                                                    ) : (
                                                        renderTimeSlotsGroup(slotsGroup)
                                                    )
                                                }
                                            </div>
                                        </>
                                    );
                                })
                            }
                            {
                                canRight && <div onClick={onRight} className={s.nextWeek}>Show next week availability</div>
                            }
                        </div>
                    </Spin>
                </div>
            </div>
        </div>
    );
}
